import { FC, useMemo } from 'react';

import { RecipientPopover } from '@bonusly/common-recipient-popover';
import { Avatar, AvatarSize, Box, Flex, Link, StyleProps } from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { useBuildRecognizeURL } from '@bonusly/url';

import { UserAvatarExcess } from '../../components/user-avatar/user-avatar-excess';
import { Route } from '../../config/router';
import { UserBonusFeedProps } from '../../types';
import { pluck } from '../../utils';
import { pathWithId } from '../../utils/api';
import { splitOverflow } from '../../utils/array';

export const DEFAULT_MAX_USERS = 5;

// Define a mapping between our component sizes and Avatar component sizes
const sizeMap: Record<'lg' | 'xl', AvatarSize> = {
	lg: 'md',
	xl: 'lg',
};

type AvatarGroupSize = 'lg' | 'xl';

interface UserAvatarGroupProps extends StyleProps {
	isPopoverDisabled?: boolean;
	max?: number;
	onAvatarClick?: (event: any) => void | VoidFunction;
	size?: AvatarGroupSize;
	users: UserBonusFeedProps[];
	recipients?: Recipient[];
}

const getMarginProps = () => {
	return {
		marginEnd: '-8px',
	};
};

export const UserAvatarGroup: FC<UserAvatarGroupProps> = ({
	isPopoverDisabled,
	max = DEFAULT_MAX_USERS,
	onAvatarClick,
	size = 'lg',
	users,
	recipients,
	...rest
}) => {
	const recognizeURL = useBuildRecognizeURL();

	const usersRecipients = users.map((user) => new Recipient(user));
	const { head, tail, doesOverflow, overflowCount } = splitOverflow(recipients || usersRecipients, { max, minOverflow: 2 });

	const overflowUserNames = useMemo(() => tail.map(pluck('displayName')), [tail]);

	return (
		<Flex dir="row" alignItems="center" {...rest}>
			{head.map((recipient) => {
				const isUser = recipient.type !== 'group';
				const LinkOrBox = isUser ? Link : Box;
				const userHref = recognizeURL(pathWithId(Route.UserProfile, recipient.id));

				return (
					<RecipientPopover key={recipient.id} recipient={recipient} isDisabled={isPopoverDisabled}>
						<LinkOrBox
							_hover={{ textDecoration: 'none' }}
							lineHeight={0}
							display="flex"
							alignItems="center"
							{...(isUser && { href: userHref })}
							{...getMarginProps()}
						>
							<Avatar name={recipient.displayName} src={recipient.imageSrc} onClick={onAvatarClick} size={sizeMap[size]} />
						</LinkOrBox>
					</RecipientPopover>
				);
			})}

			{doesOverflow && <UserAvatarExcess count={overflowCount} userNames={overflowUserNames} size={sizeMap[size]} {...getMarginProps()} />}
		</Flex>
	);
};
